/**
 * MovieCard component for Movies and TV Shows.
 * Features entrance animation and dynamic state with Lottie spotlight background.
 *
 * Dynamic state composition (from Figma):
 * 1. Lottie spotlight animation rendered to a hidden container
 * 2. Cover image blurred at 60, positioned behind main poster
 * 3. Lottie SVG serialized per-frame as CSS mask-image on the blurred cover
 * 4. Entire composition at 60% opacity
 * 5. Size: 390×380px, positioned at bottom of card
 */

import React, { FC, useRef, useEffect, useCallback } from 'react';
import { styled } from 'linaria/react';
import lottie, { AnimationItem } from 'lottie-web';

import spotlightsData from '../../../assets/lottie/spotlights.json';
import { useCardAnimation } from '../../../hooks/useCardAnimation';
import { MovieCardProps } from '../types';
import { BORDER_RADIUS, CARD_ENTRANCE } from '../styled/tokens';

// Aspect ratio: 41w / 61h
const POSTER_ASPECT_RATIO = 41 / 61;

const MOVIE_DYNAMIC = {
  /** Spotlight background size */
  bgWidth: 390,
  bgHeight: 380,
  /** Blur applied to the Lottie base layer (visible underneath) */
  lottieBlur: 4,
  /** Blur applied to the masked cover image */
  coverBlur: 60,
  /** Opacity of the entire dynamic composition */
  compositionOpacity: 0.6,
  /** Fade duration */
  fadeDuration: 0.8,
} as const;

/* ─── styled components ─── */

const StyledMovieContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 100%;
  overflow: visible;
`;

const StyledLink = styled.a`
  display: block;
  text-decoration: none;
  cursor: pointer;
`;

/* ─── entrance wrapper ─── */

const StyledPosterWrapper = styled.div<{ isEntranceActive: boolean }>`
  position: relative;
  z-index: 1;
  opacity: ${({ isEntranceActive }) => (isEntranceActive ? 1 : CARD_ENTRANCE.contentOpacity)};
  transform: ${({ isEntranceActive }) =>
    isEntranceActive
      ? 'translateY(0) scale(1)'
      : `translateY(${CARD_ENTRANCE.contentOffsetY}px) scale(${CARD_ENTRANCE.contentInitialScale})`};
  transition: opacity ${CARD_ENTRANCE.opacityDuration}s ${CARD_ENTRANCE.easing},
    transform ${CARD_ENTRANCE.scaleDuration}s ${CARD_ENTRANCE.easing};
  will-change: opacity, transform;
`;

/* ─── dynamic state: lottie-masked blurred background ─── */

const StyledDynamicBackground = styled.div<{ isDynamic: boolean }>`
  position: absolute;
  bottom: 0;
  left: 50%;
  width: ${MOVIE_DYNAMIC.bgWidth}px;
  height: ${MOVIE_DYNAMIC.bgHeight}px;
  transform: translateX(-50%);
  z-index: 0;
  pointer-events: none;
  opacity: ${({ isDynamic }) => (isDynamic ? MOVIE_DYNAMIC.compositionOpacity : 0)};
  transition: opacity ${MOVIE_DYNAMIC.fadeDuration}s ease;
  overflow: hidden;
`;

/** Hidden container — Lottie renders here for SVG serialization */
const StyledLottieSource = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  pointer-events: none;
  visibility: hidden;
  overflow: hidden;

  & > svg {
    width: 100%;
    height: 100%;
  }
`;

/** Visible Lottie layer (base, blur 4) */
const StyledLottieDisplay = styled.div`
  position: absolute;
  inset: 0;
  filter: blur(${MOVIE_DYNAMIC.lottieBlur}px);
  z-index: 0;
  pointer-events: none;
  overflow: hidden;

  & > svg {
    width: 100%;
    height: 100%;
  }
`;

/** Blurred cover — masked by Lottie SVG via mask-image */
const StyledMaskedCover = styled.div<{ bgImage?: string }>`
  position: absolute;
  inset: 0;
  background-image: ${({ bgImage }) => (bgImage ? `url(${bgImage})` : 'none')};
  background-size: cover;
  background-position: center;
  filter: blur(${MOVIE_DYNAMIC.coverBlur}px);
  z-index: 1;
  -webkit-mask-size: 100% 100%;
  mask-size: 100% 100%;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
`;

/* ─── poster ─── */

const StyledPoster = styled.div`
  width: 242px;
  height: 360px;
  aspect-ratio: ${POSTER_ASPECT_RATIO};
  border-radius: ${BORDER_RADIUS.md}px;
  overflow: hidden;
  position: relative;
  z-index: 1;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
`;

const StyledPosterImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StyledFallback = styled.div<{ bgColor: string }>`
  width: 100%;
  height: 100%;
  background-color: ${({ bgColor }) => bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MovieCard: FC<MovieCardProps> = ({
  title,
  imageUrl,
  backgroundColor,
  externalLink,
  className,
  disableAnimation,
}) => {
  const { containerRef, isEntranceActive, isDynamicActive } = useCardAnimation({
    threshold: 0.7,
    disabled: disableAnimation,
    entranceDuration: CARD_ENTRANCE.scaleDuration * 1000,
  });

  const lottieSourceRef = useRef<HTMLDivElement>(null);
  const lottieDisplayRef = useRef<HTMLDivElement>(null);
  const maskedCoverRef = useRef<HTMLDivElement>(null);
  const sourceAnimRef = useRef<AnimationItem | null>(null);
  const displayAnimRef = useRef<AnimationItem | null>(null);

  // Initialize two lottie instances: one hidden (for mask serialization), one visible (blur 4 display)
  useEffect(() => {
    if (disableAnimation) return undefined;

    if (lottieSourceRef.current) {
      sourceAnimRef.current = lottie.loadAnimation({
        container: lottieSourceRef.current,
        renderer: 'svg',
        loop: true,
        autoplay: false,
        animationData: spotlightsData,
      });
    }

    if (lottieDisplayRef.current) {
      displayAnimRef.current = lottie.loadAnimation({
        container: lottieDisplayRef.current,
        renderer: 'svg',
        loop: true,
        autoplay: false,
        animationData: spotlightsData,
      });
    }

    return () => {
      sourceAnimRef.current?.destroy();
      sourceAnimRef.current = null;
      displayAnimRef.current?.destroy();
      displayAnimRef.current = null;
    };
  }, [disableAnimation]);

  // Serialize Lottie SVG as mask-image on each animation frame
  const updateMask = useCallback(() => {
    const svg = lottieSourceRef.current?.querySelector('svg');
    if (svg && maskedCoverRef.current) {
      const serialized = new XMLSerializer().serializeToString(svg);
      const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(serialized)}`;
      maskedCoverRef.current.style.webkitMaskImage = `url("${dataUrl}")`;
      maskedCoverRef.current.style.maskImage = `url("${dataUrl}")`;
    }
  }, []);

  // Control playback + mask sync
  useEffect(() => {
    if (!isDynamicActive) {
      sourceAnimRef.current?.stop();
      displayAnimRef.current?.stop();
      if (maskedCoverRef.current) {
        maskedCoverRef.current.style.webkitMaskImage = 'none';
        maskedCoverRef.current.style.maskImage = 'none';
      }
      return undefined;
    }

    sourceAnimRef.current?.play();
    displayAnimRef.current?.play();

    // Update mask on each lottie frame
    const onEnterFrame = (): void => {
      updateMask();
    };

    sourceAnimRef.current?.addEventListener('enterFrame', onEnterFrame);

    // Initial mask update
    updateMask();

    return () => {
      sourceAnimRef.current?.removeEventListener('enterFrame', onEnterFrame);
    };
  }, [isDynamicActive, updateMask]);

  const posterContent = (
    <StyledPosterWrapper isEntranceActive={isEntranceActive}>
      <StyledPoster>
        {imageUrl ? (
          <StyledPosterImage src={imageUrl} alt={title} loading="lazy" />
        ) : (
          <StyledFallback bgColor={backgroundColor ?? '#e8e4dc'} />
        )}
      </StyledPoster>
    </StyledPosterWrapper>
  );

  return (
    <StyledMovieContainer className={className} ref={containerRef}>
      {/* Dynamic state: lottie spotlight masking blurred cover */}
      <StyledDynamicBackground isDynamic={isDynamicActive}>
        {/* Hidden lottie for SVG serialization → mask-image */}
        <StyledLottieSource ref={lottieSourceRef} />

        {/* Visible lottie base layer (blur 4) */}
        <StyledLottieDisplay ref={lottieDisplayRef} />

        {/* Blurred cover masked by lottie shapes */}
        <StyledMaskedCover ref={maskedCoverRef} bgImage={imageUrl ?? undefined} />
      </StyledDynamicBackground>

      {externalLink ? (
        <StyledLink href={externalLink} target="_blank" rel="noopener noreferrer">
          {posterContent}
        </StyledLink>
      ) : (
        posterContent
      )}
    </StyledMovieContainer>
  );
};

export default MovieCard;
